import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import HeaderComponentContainer from '@Containers/Header/HeaderComponentContainer';
import Footer from '@Components/Footer';
import useDeleteFocusManager from '@Hooks/useDeleteFocusManager';
import { buildAlertDeleteButtonId } from '@Pages/Alerts/AlertsUtils';
import AlertDeleteConfirmationModal from '@Pages/Alerts/AlertsComponents/AlertDeleteConfirmationModal/AlertDeleteConfirmationModal';
import NoAlerts from './AlertsComponents/NoAlerts';
import GroupsList from './AlertsComponents/GroupsList';

import './Alerts.scss';

// Alerts icon in the header, used as a fallback focus target when the deleted item is not in the list anymore (e.g. when deleting the last alert in a group)
const FALLBACK_FOCUS_ID = 'alerts-icon';

export default function Alerts({
  groupsIds,
  renderedAlertsIds,
  showDeleteModal,
  alertIdToDelete,
  handleViewDeleteModal,
  removeAlert,
  isDeleteInProgress,
  deletingAlertId,
  statusMessage = '',
  setStatusMessage = () => {},
}) {
  const result = groupsIds && (groupsIds.count() ? <GroupsList groupsIds={groupsIds} /> : <NoAlerts />);

  const deleteButtonIds = useMemo(() => {
    if (!renderedAlertsIds) return [];

    const alertIds = renderedAlertsIds.toJS();

    return alertIds.map(buildAlertDeleteButtonId);
  }, [renderedAlertsIds]);

  const deletingId = deletingAlertId ? buildAlertDeleteButtonId(deletingAlertId) : null;

  useDeleteFocusManager({
    itemIds: deleteButtonIds,
    deletingId,
    isDeleting: !!isDeleteInProgress,
    fallbackFocusId: FALLBACK_FOCUS_ID,
  });

  return (
    // FR-1.1/FR-3.6: `/alerts` is a covered page, so Main.jsx now suppresses its sibling header for
    // this route. Alerts therefore renders the header INSIDE its own scroll root — a `.react-page`
    // (`react-page--header-inside` = full-height, not the default 90vh clamp) whose first child is
    // the header. This keeps exactly one `#header` + one `#main-content` on the page and makes this
    // one section the sole scroller (AR-1.3). Nothing is sticky on Alerts (matrix); the header just
    // scrolls away with the list.
    //
    // A11y: this wrapper MUST stay a `<div>`, never a `<section>`/`<article>`/`<aside>`/`<nav>`/
    // `<main>`. Per HTML-AAM the Leyden `<Header>` (a bare `<header>` with no explicit `role`) only
    // maps to the `banner` landmark when it is NOT a descendant of one of those sectioning-content
    // elements — nesting it under a `<section>` here would silently strip that landmark (same fix
    // as Page.jsx).
    <div className="react-page react-page--header-inside alerts-react-page">
      <HeaderComponentContainer type="main" />
      <div className="alerts-page__container alerts-container">
        {showDeleteModal && (
          <AlertDeleteConfirmationModal
            onCancel={() => handleViewDeleteModal(false)}
            onConfirm={() => {
              setStatusMessage('Deleting alert.');
              Promise.resolve(removeAlert(alertIdToDelete)).then((deleted) => {
                if (deleted) {
                  const remaining = Math.max(0, (renderedAlertsIds?.size || 0) - 1);
                  setStatusMessage(`Alert deleted. ${remaining} alert${remaining === 1 ? '' : 's'} remaining.`);
                } else {
                  setStatusMessage('Unable to delete alert.');
                }
              });
              handleViewDeleteModal(false);
            }}
          />
        )}
        <main id="main-content" className="alerts-content" aria-labelledby="alerts-title">
          <section className="rx-list-wrapper">
            <div className="header-container--border-bottom u-els-padding-top-1x u-els-padding-bottom-1x">
              <h1 id="alerts-title" tabIndex="-1" className="alerts-heading u-els-font-size-h3 rx-left-gutter">
                Alerts
              </h1>
            </div>
            <div role="status" aria-live="polite" aria-atomic="true" className="sr-only" data-testid="alerts-status">
              {statusMessage}
            </div>
            <div className="alerts-items-wrapper rx-list-wrapper rx-left-gutter rx-right-gutter">{result}</div>
          </section>
        </main>
        <Footer />
      </div>
    </div>
  );
}

Alerts.propTypes = {
  groupsIds: PropTypes.instanceOf(List),
  renderedAlertsIds: PropTypes.instanceOf(List),
  showDeleteModal: PropTypes.bool,
  alertIdToDelete: PropTypes.string,
  handleViewDeleteModal: PropTypes.func,
  removeAlert: PropTypes.func,
  isDeleteInProgress: PropTypes.bool,
  deletingAlertId: PropTypes.string,
  statusMessage: PropTypes.string,
  setStatusMessage: PropTypes.func,
};
